// =============================================================================
// participations-client.ts — Client dédié « résumé participations par SIRET » (T2-REGISTRE).
// =============================================================================
// Miroir de l'endpoint batch-read `POST /admin/campaigns/participations/summary` du BC campaign
// (CampaignSupplierParticipationsController, Be-CAMPAIGN-BY-SIRET, RILASCIATO commit 6437864).
// Client DÉDIÉ (et non une méthode du gros campaign-client) : la composition du registre
// supplier-reference (colonnes « Participations » + « Dernière date de référencement ») est ainsi
// EXPLICITE et découplée du cycle de vie campagne. Comme les autres clients UI, expose une INTERFACE
// consommée par un hook TanStack ; l'impl HTTP réelle est substituée derrière le MÊME DI seam.
//
// INTRA-BC / ISOLATION : la donnée campagne entre dans le registre par COMPOSITION UI (2 appels réseau
// distincts), JAMAIS par une requête cross-schéma côté serveur. Les SIRET sont des identifiants opaques
// déjà connus de l'admin appelant — la réponse ne porte AUCUNE PII (compteur + date agrégée seulement).

import { HttpApiError } from '@/api/http-error';
import type {
  CampaignStateDto,
  CycleNumberDto,
  ParticipationStateDto,
} from '@/api/campaign-client';

/**
 * Résumé des participations d'un SIRET (miroir HTTP de `SupplierParticipationSummaryView`, DTO FIGÉ
 * D1). `participationCount` = nombre de participations ; `lastReferencedAt` = MAX(submittedAt) sur les
 * participations (ISO 8601), `null` si aucun référencement réalisé. Une ligne PAR SIRET demandé (un
 * SIRET sans participation → `participationCount: 0`, `lastReferencedAt: null`).
 */
export interface SupplierParticipationSummaryDto {
  readonly supplierSiret: string;
  readonly participationCount: number;
  readonly lastReferencedAt: string | null;
}

/**
 * Borne du batch-read `POST summary` (parité serveur `MAX_SUMMARY_SIRETS`). Au-delà, l'adaptateur
 * HTTP DÉCOUPE la demande en lots (chunking UI, GATE D) puis fusionne les résultats.
 */
export const MAX_SUMMARY_SIRETS = 500;

/**
 * Référence minimale d'une campagne OUVERTE d'un SIRET (miroir HTTP de `SupplierOpenCampaignRef`, DTO
 * FIGÉ D1). Sert à rédiger l'avertissement contextuel du transfert MASTER (DBU §2.2 l.712) : intitulé
 * + état (parité `CampaignStateDto`). AUCUNE PII (identifiants + libellé de campagne seulement).
 */
export interface SupplierOpenCampaignRefDto {
  readonly campaignId: string;
  readonly intitule: string;
  readonly state: CampaignStateDto;
}

/**
 * Flag « campagne ouverte » d'un SIRET au sens PROTECTEUR (miroir HTTP de `SupplierOpenCampaignFlagView`,
 * DTO FIGÉ D1 — reproduit à l'identique, ne pas re-modeler). `hasOpenCampaign` = le fournisseur participe
 * à ≥ 1 campagne LANCÉE et NON TERMINÉE ; `openCampaigns` = la liste minimale de ces campagnes (vide si
 * `hasOpenCampaign === false`). Consommé à l'ouverture du dialog de transfert MASTER (avertissement, DBU
 * §2.2 l.712) : CAUTION, jamais un blocage. JAMAIS 404 (un SIRET sans campagne ouverte = état légitime).
 */
export interface SupplierOpenCampaignFlagDto {
  readonly supplierSiret: string;
  readonly hasOpenCampaign: boolean;
  readonly openCampaigns: readonly SupplierOpenCampaignRefDto[];
}

/**
 * Une campagne dans la chronologie des participations d'un SIRET (fiche T8, DBU §2.2 l.694). Miroir
 * HTTP de `SupplierCampaignParticipationView` (DTO FIGÉ). `currentCycle` null = campagne non lancée
 * ou terminale ; `lastRelevantAt` JAMAIS null (garantie serveur : submitted ?? relance ?? invitation
 * ?? création campagne). AUCUNE PII (données OWNED campaign + SIRET opaque déjà connu de l'admin).
 */
export interface SupplierCampaignParticipationDto {
  readonly participationId: string;
  readonly campaignId: string;
  readonly intitule: string;
  readonly campaignState: CampaignStateDto;
  readonly participationState: ParticipationStateDto;
  readonly currentCycle: CycleNumberDto | null;
  readonly lastRelevantAt: string;
}

/**
 * Enveloppe fiche : chronologie complète des participations d'un SIRET (miroir HTTP de
 * `SupplierParticipationsBySiretView`, DTO FIGÉ). Le tableau est TRIÉ « plus récente d'abord » côté
 * serveur — le front ne re-trie pas (aucune règle métier UI). JAMAIS 404 (un SIRET sans participation
 * → `{ supplierSiret, participations: [] }`).
 */
export interface SupplierParticipationsBySiretDto {
  readonly supplierSiret: string;
  readonly participations: readonly SupplierCampaignParticipationDto[];
}

/**
 * Erreur applicative renvoyée par l'API admin campaign (HTTP 4xx). L'UI l'affiche telle quelle ;
 * elle ne réinterprète jamais la règle métier (autorité = API). `status` = code HTTP quand connu.
 */
export class ParticipationsApiError extends HttpApiError {
  public constructor(message: string, status?: number) {
    super(message, status, 'ParticipationsApiError');
  }
}

/**
 * Contrat du client « résumé participations ». Aucune règle métier : la lecture (comptage,
 * MAX(submittedAt)) est l'autorité de l'API. L'UI relaie l'intention (les SIRET de la page courante).
 */
export interface ParticipationsClient {
  /**
   * POST /admin/campaigns/participations/summary — résumé bulk par SIRET (≤ 500 par requête). Renvoie
   * une ligne par SIRET demandé. `sirets` vide → `[]` sans appel réseau. Au-delà de la borne, l'impl
   * chunk et fusionne (chunking UI).
   */
  summarizeParticipations(
    sirets: readonly string[],
  ): Promise<readonly SupplierParticipationSummaryDto[]>;

  /**
   * GET /admin/campaigns/participations/by-siret/:siret/open-campaign — flag « campagne ouverte »
   * (avertissement transfert MASTER, DBU §2.2 l.712). JAMAIS 404 : un SIRET sans campagne ouverte
   * renvoie `{ supplierSiret, hasOpenCampaign: false, openCampaigns: [] }`. Lecture seule (aucun audit).
   */
  getOpenCampaignFlag(siret: string): Promise<SupplierOpenCampaignFlagDto>;

  /**
   * GET /admin/campaigns/participations/by-siret/:siret — chronologie des participations d'un SIRET
   * (fiche T8, DBU §2.2 l.694). JAMAIS 404 : un SIRET sans participation renvoie
   * `{ supplierSiret, participations: [] }`. Lecture seule (aucun audit). Tri « plus récente d'abord »
   * = autorité serveur (le front n'a pas à re-trier). Endpoint DISTINCT de `getOpenCampaignFlag`
   * (open-campaign = sous-ensemble « ouvertes » ; by-siret = chronologie complète).
   */
  getParticipationsBySiret(
    siret: string,
  ): Promise<SupplierParticipationsBySiretDto>;
}
